import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

// The instance record. Each network gets its own deployment, so there is exactly
// one and it is never selectable — but its display name is editable, because
// APP_INSTANCE_NAME is read only while the table is empty and a wrong name would
// otherwise need SQL against the deployed database. Renaming is superadmin-only
// server-side.
export type InstanceNetwork = {
  id: string
  name: string
  status: 'active' | 'inactive'
  metadata: Record<string, unknown>
  /**
   * Minutes of inactivity before a session ends, instance-wide.
   *
   * Null means the deployment has not configured one, and the app falls back to
   * its own default — it does NOT mean the timeout is off. Resolve it through
   * `resolveIdleMinutes` in `@/lib/idleClock` rather than reading it directly,
   * which also clamps a value written straight to the database.
   */
  idleTimeoutMinutes: number | null
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`API ${init?.method ?? 'GET'} ${path} failed (${res.status}): ${text}`)
  }
  return res.json() as Promise<T>
}

export const networksApi = {
  current: () => request<InstanceNetwork>('/networks/current'),
  // Both fields are optional and independent: the rename control sends only
  // `name`, the session setting only `idleTimeoutMinutes`. Sending
  // `idleTimeoutMinutes: null` clears it back to "not configured"; omitting it
  // leaves the stored value alone. The two are not interchangeable.
  update: (input: { name?: string; idleTimeoutMinutes?: number | null }) =>
    request<InstanceNetwork>('/networks/current', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
}
